import { GridTable } from "@ctsu/ctsu-mui-grid";
import { ProgressIndicator } from "@ctsu/react-ctsu-ui";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SiteInfoButton from "components/common/SiteInfoButton";
import TabedLink from "components/common/TabedLink";
import Text, { text } from "components/common/Text";
import { Form, Formik } from "formik";
import { useGetAlphaIds, useGetUserGroup } from "helpers/getAppConfig";
import makeAjaxRequest from "helpers/makeAjaxRequest";
import { getEncodedQueryParams } from "helpers/utils";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdvanceSearch from "./AdvanceSearch";
import OldOrNewSiteModal from "./OldOrNewSiteModal";

const { REACT_APP_ORIGIN } = process.env;
const queryURL = `${REACT_APP_ORIGIN}site-data-service/v1/sites/search`;
const getEncodedUrl = (siteCtepId, userGroup) =>
  getEncodedQueryParams("/institution/roster/affiliation", `ctepid=${siteCtepId}&userGroup=${userGroup}`);

function Toggle({ modal, setModal }) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div className="toggle-switch float-right mt-n4">
      {location.state && location.state.ctepId && (
        <button
          className="fn-btn mx-2"
          type="button"
          onClick={() => navigate(-1)}
        >
          {text("institution.search.search_box.return_institution_roster")}
        </button>
      )}
      <button type="button" onClick={() => setModal(!modal)} className=" fn-btn" name="toggleSwitch" id="toggleSwitch">
        <label className="toggle-switch-label" htmlFor="toggleSwitch">
          {modal
            ? text("institution.search.search_box.show_simple_search")
            : text("institution.search.search_box.show_advanced_search")}
        </label>
      </button>
    </div>
  );
}

export default function Searchbox() {
  const [result, setResult] = useState(null);
  const [isLoading, setIsloading] = useState(false);
  const [searchText, setSearchtext] = useState("");
  const [modal, setModal] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({});
  const [isInactiveSite, setIsInactiveSite] = useState(null);
  const searchBoxRef = useRef(null);
  const onlyAlphaIds = useGetAlphaIds();
  const userGroup = useGetUserGroup();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (searchBoxRef.current) {
      searchBoxRef.current.focus();
    }
  }, []);

  function callOnRowClick(data) {
    const path = getEncodedQueryParams(
      "/institution/roster/affiliation",
      `ctepid=${data.siteCtepId}&userGroup=${userGroup}`
    );
    navigate(path);
  }
  function queryInstitution(queryParams, callback) {
    setIsloading(true);
    makeAjaxRequest.postWithAuth(queryURL, queryParams).then(response => {
      setResult(response);
      setIsloading(false);
      if (response && response.length === 1) {
        // if the iste has newCtepId, set the state to prompt user to pick either new or old site
        if (response[0].newCtepId) {
          setIsInactiveSite(response[0]);
        } else {
          callOnRowClick(response[0]);
        }
      } else {
        setModal(true);
      }
    }).then(() => callback()).catch((err) => {
      setIsloading(false);
    });
  }

  const onClickOfRow = data => {
    if (data.newCtepId) {
      setIsInactiveSite(data);
    } else {
      callOnRowClick(data);
    }
  };

  function initiateSearch(inputParams, callback) {
    if (inputParams) {
      const params = {};
      const queryParams = inputParams.trim();
      const Reg = new RegExp(/\d+/g);
      const searchBySiteCtepId = onlyAlphaIds.get(queryParams.toUpperCase()) || Reg.test(queryParams) ||
        queryParams.length < 3;

      if (searchBySiteCtepId) {
        params.siteCtepId = queryParams;
      } else {
        params.instName = queryParams;
      }
      setSearchCriteria(params);
      queryInstitution(params, callback);
    } else {
      setModal(true);
    }
    return true;
  }

  function closeAndClear() {
    setIsInactiveSite(null);
  }

  const columns = [
    {
      name: "siteCtepId",
      label: text("institution.search.institution_ctep_id"),
      align: "left",
      renderCell: data => (
        <>
          <SiteInfoButton siteId={data.siteCtepId} smallSize />
          <span className="mr-1 color-turquoise">|</span>
          {data.newCtepId
            ? (
              <button type="button" className="fn-btn" onClick={() => onClickOfRow(data)}>
                {data.siteCtepId}
              </button>
            )
            : <TabedLink url={getEncodedUrl(data.siteCtepId, userGroup)} text={data.siteCtepId} />}
        </>
      )
    },

    {
      name: "instName",
      label: text("institution.search.institution_name"),
      align: "left"
    },
    {
      name: "zipCode",
      label: text("institution.search.zip_code"),
      align: "left"
    },
    {
      name: "cityName",
      label: text("institution.search.city"),
      align: "center"
    },
    {
      name: "stateName",
      label: text("institution.search.state"),
      align: "center"
    },
    {
      name: "groupId",
      label: text("institution.search.group_id"),
      align: "center"
    }
  ];

  return (
    <>
      <Toggle {...{ modal, setModal }} />
      <div>
        {modal
          ? (
            <div className="item">
              <>
                <h1 className="align-center content-page-title">{text("institution.search.advanced_inst_search")}</h1>
                <div className="mt-3" />
                <AdvanceSearch
                  searchText={searchText}
                  setSearchtext={setSearchtext}
                  hasResults={result && result.length}
                  searchCriteria={searchCriteria}
                  isLoading={isLoading}
                  queryInstitution={queryInstitution}
                />
                {result && result.length > 0 && (
                  <>
                    <div className="content-section-title">Results</div>
                    <GridTable
                      id="orgId"
                      tag="inst-search"
                      title={`${text("institution.search.for")}${searchText}${text("common.single_quote")}`}
                      rows={result}
                      columns={columns}
                      enableToolbar={false}
                      sortable
                      recordPerPage={25}
                    />
                  </>
                )}
              </>
            </div>
          )
          : (
            <div className="item">
              <>
                <div className="align-center content-page-title">
                  <Text bundle="institution.finder.header" />
                </div>
                <Formik
                  initialValues={{
                    siteCtepId: ""
                  }}
                  onSubmit={(values, formikActions) => {
                    const callback = () => {
                      formikActions.setSubmitting(false);
                      setSearchtext(values.siteCtepId);
                    };
                    initiateSearch(values.siteCtepId, callback);
                  }}
                >
                  {props => (
                    <>
                      <h1 className="sr-only">Institution Finder</h1>
                      <Form>
                        <div className={`row ${isLoading ? "nprogress-custom-parent" : ""}`}>
                          <div className="col-3" />
                          <div className="searchBox col-6">
                            <FontAwesomeIcon icon={faSearch} className="color-primary-blue" />
                            <label htmlFor="searchBox" className="invisible">
                              {text("institution.search.title")}
                            </label>
                            <input
                              id="searchBox"
                              data-testid="inst-search-box"
                              type="text"
                              ref={searchBoxRef}
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              value={props.values.siteCtepId}
                              name="siteCtepId"
                              placeholder="Enter an Institution Name or CTEP ID"
                            />

                            <button
                              data-testid="inst-search-btn"
                              type="submit"
                              className="searchBox-go fn-btn"
                              disabled={isLoading}
                            >
                              {text("common.go")}
                            </button>
                          </div>
                          {isLoading && (
                            <div className="content-middle">
                              <ProgressIndicator />
                            </div>
                          )}
                        </div>
                      </Form>
                    </>
                  )}
                </Formik>
                <div className="mt-3" />
                <div className="align-center col-6 m-auto">
                  <Text bundle="institution.finder.subheader" />
                </div>
              </>
            </div>
          )}
      </div>
      {isInactiveSite && (
        <OldOrNewSiteModal onClose={closeAndClear} siteInfoObject={isInactiveSite} userGroup={userGroup} />
      )}
    </>
  );
}
